import React, { useCallback, useEffect } from 'react';
import { useConfiguration } from '../../context/configurationContext';
import loadable from '@loadable/component';
import { useSelector } from 'react-redux';
import {
  detectBrowserLocale,
  getLocaleFromCurrentUser,
} from '../../util/genericHelpers';
import { useConsent } from 'react-hook-consent';
import { isActiveRoleHost } from '../../util/activeRole';

const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

const LINE_OFFICIAL_ACCOUNT_URL = 'https://page.line.me/106vfxun';

const addLineOfficialAccountLink = socialMediaLinks => {
  const links = Array.isArray(socialMediaLinks) ? socialMediaLinks : [];
  const hasLineLink = links.some(link => link?.link?.platform === 'line');

  if (hasLineLink) {
    return links;
  }

  return [
    ...links,
    {
      blockType: 'socialMediaLink',
      link: {
        fieldType: 'socialMediaLink',
        platform: 'line',
        url: LINE_OFFICIAL_ACCOUNT_URL,
      },
    },
  ];
};

/**
 * Safe helper to read locale from localStorage (client only)
 */
const getLocaleFromLocalStorage = () => {
  if (typeof window !== 'undefined') {
    return window.localStorage.getItem('locale');
  }
  return null;
};

// Removes a whole markdown list line like: "- [Post a new listing](...)"
const removeMarkdownLinkLine = (content = '', linkText) => {
  const escaped = String(linkText).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`^\\s*[-*+]\\s*\\[${escaped}\\]\\([^\\)]*\\)\\s*\\n?`, 'gmi');
  return String(content).replace(re, '');
};

const FooterComponent = () => {
  const { footer = {}, topbar } = useConfiguration();
  const { currentUser } = useSelector(state => state.user);
  const isHost = isActiveRoleHost(currentUser);

  const { toggleBanner, isBannerOpen } = useConsent();

  const isLoggedIn = !!currentUser?.id;

  const savedLocale = isLoggedIn ? getLocaleFromCurrentUser(currentUser) : null;
  const localStorageLocale = getLocaleFromLocalStorage();
  const detectedLocale = detectBrowserLocale();

  // ✅ Locale priority (same as IntlFromUser)
  const locale = isLoggedIn
    ? savedLocale || localStorageLocale || detectedLocale || 'en'
    : localStorageLocale || detectedLocale || 'en';

  // ✅ Fixed: Opens modal/banner without resetting consent state
  const handleOpenConsent = useCallback(
    e => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Method 1: Try using the global bridge function (preferred)
      if (typeof window !== 'undefined' && typeof window.__openConsent === 'function') {
        try {
          window.__openConsent();
          return true;
        } catch (err) {
          console.warn('[Footer] window.__openConsent() failed:', err);
        }
      }

      // Method 2: Fallback - directly use toggleBanner from useConsent
      // ✅ IMPORTANT: Do NOT call setConsent() - this preserves user's choices
      try {
        if (!isBannerOpen) {
          toggleBanner();
        }
        return true;
      } catch (err) {
        console.error('[Footer] toggleBanner() failed:', err);
      }

      console.warn('[Footer] Could not open consent modal.');
      return false;
    },
    [toggleBanner, isBannerOpen]
  );

  /**
   * ✅ Handle hash-based navigation to #cookie-preferences
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const maybeOpenFromHash = () => {
      try {
        if (window.location && window.location.hash === '#cookie-preferences') {
          setTimeout(() => {
            const opened = handleOpenConsent();

            // Clear the hash so it doesn't stay in the URL
            if (opened) {
              const { pathname, search } = window.location;
              window.history.replaceState(null, '', `${pathname}${search}`);
            }
          }, 150);
        }
      } catch (err) {
        console.error('[Footer] Failed handling cookie-preferences hash', err);
      }
    };

    maybeOpenFromHash();
    window.addEventListener('hashchange', maybeOpenFromHash);
    return () => window.removeEventListener('hashchange', maybeOpenFromHash);
  }, [handleOpenConsent]);

  /**
   * ✅ Intercept direct clicks on <a href="#cookie-preferences">
   */
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const onClick = e => {
      const target = e.target;
      if (!target) return;

      const anchor = target.closest ? target.closest('a') : null;
      if (!anchor) return;

      const href = anchor.getAttribute('href') || '';

      if (href === '#cookie-preferences' || href.endsWith('#cookie-preferences')) {
        e.preventDefault();
        e.stopPropagation();
        handleOpenConsent(e);
      }
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [handleOpenConsent]);

  // If footer asset is not set, let's not render Footer at all.
  if (Object.keys(footer).length === 0) {
    return null;
  }

  /**
   * ✅ Step 1: Do ONLY non-language mutations here:
   * - Remove "Post a new listing" for non-hosts (handles both EN/TH text safely)
   * - Inject cookie preferences link
   *
   * ❌ Do NOT translate here (that was breaking your language logic).
   */
  const footerWithCookieLink = {
    ...footer,
    socialMediaLinks: addLineOfficialAccountLink(footer.socialMediaLinks),
    blocks: (footer.blocks || []).map(block => {
      // ✅ Hide "Post a new listing" for non-hosts (works whether content is EN or TH)
      if (block.blockId === 'general' && block.text?.content && !isHost) {
        const removedEN = removeMarkdownLinkLine(block.text.content, 'Post a new listing');
        const removedTH = removeMarkdownLinkLine(removedEN, 'ลงประกาศสถานที่');

        return {
          ...block,
          text: {
            ...block.text,
            content: removedTH,
          },
        };
      }

      // ✅ Inject cookie preferences link into Legal block (terms-and-privacy)
      if (block.blockId === 'terms-and-privacy' && block.text?.content) {
        // avoid duplicates
        if (block.text.content.includes('(#cookie-preferences)')) {
          return block;
        }

        const isTH = (locale || '').toLowerCase().startsWith('th');
        const label = isTH ? 'ตั้งค่าคุกกี้' : 'Cookies Preferences';

        return {
          ...block,
          text: {
            ...block.text,
            content: `${block.text.content}\n- [${label}](#cookie-preferences)`,
          },
        };
      }

      return block;
    }),
  };

  /**
   * ✅ Step 2: Apply Thai translations ONLY when locale is Thai
   */
  let localizedFooter = footerWithCookieLink;

  if (locale?.toLowerCase().startsWith('th')) {
    const updatedBlocks = (footerWithCookieLink.blocks || []).map(block => {
      // Translate headings + link labels (keep URLs same)
      if (block.blockId === 'general' && block.text?.content) {
        let updatedContent = block.text.content
          .replace('**Company**', '**บริษัท**')
          .replace('[About us]', '[เกี่ยวกับเรา]')
          .replace('[See all listings]', '[ดูสถานที่ทั้งหมด]')
          .replace('[Post a new listing]', '[ลงประกาศสถานที่]')
          .replace('[Contact Us]', '[ติดต่อเรา]');

        // ✅ ensure hidden for non-hosts even in TH mode (safe if already removed)
        if (!isHost) {
          updatedContent = removeMarkdownLinkLine(updatedContent, 'ลงประกาศสถานที่');
          updatedContent = removeMarkdownLinkLine(updatedContent, 'Post a new listing');
        }

        return {
          ...block,
          text: {
            ...block.text,
            content: updatedContent,
          },
        };
      }

      if (block.blockId === 'terms-and-privacy' && block.text?.content) {
        const updatedContent = block.text.content
          .replace('**Legal**', '**กฎหมาย**')
          .replace('[Terms of Service]', '[ข้อกำหนดการให้บริการ]')
          .replace('[Privacy Policy]', '[นโยบายความเป็นส่วนตัว]')
          .replace('[Host Agreement]', '[ข้อตกลงสำหรับโฮสต์]');
        // Cookie label already localized during injection

        return {
          ...block,
          text: {
            ...block.text,
            content: updatedContent,
          },
        };
      }

      if (block.blockId === 'helpful-resources' && block.text?.content) {
        const updatedContent = block.text.content
          .replace('**Helpful Resources**', '**แหล่งข้อมูลที่เป็นประโยชน์**')
          .replace('[Shoot Responsibly]', '[ถ่ายทำอย่างรับผิดชอบ]')
          .replace('[Film Permits]', '[ใบอนุญาตถ่ายทำ]')
          .replace('[TFO Accredited Fixers]', '[รายชื่อ Fixer ที่ได้รับการรับรองจาก TFO]');

        return {
          ...block,
          text: {
            ...block.text,
            content: updatedContent,
          },
        };
      }

      return block;
    });

    localizedFooter = {
      ...footerWithCookieLink,
      slogan: footerWithCookieLink?.slogan?.content
        ? {
            ...footerWithCookieLink.slogan,
            content: 'ค้นหาและจองสถานที่ถ่ายทำที่ไม่เหมือนใครได้ภายในไม่กี่นาที',
          }
        : footerWithCookieLink.slogan,
      copyright: footerWithCookieLink?.copyright?.content
        ? {
            ...footerWithCookieLink.copyright,
            content: '© 2026 SceneAccess Co., Ltd. สงวนลิขสิทธิ์',
          }
        : footerWithCookieLink.copyright,
      blocks: updatedBlocks,
    };
  }

  const footerSection = {
    ...localizedFooter,
    sectionId: 'footer',
    sectionType: 'footer',
    linkLogoToExternalSite: topbar?.logoLink,
  };

  return <SectionBuilder sections={[footerSection]} />;
};

export default FooterComponent;
